import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

// MUI
import { Box, InputLabel, TextField, Typography } from '@mui/material';

// Shared components
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';

// Local
import OLCBaseModal from '../../BaseModal';
import DatePicker from '~/components/General/OlcDatePicker';
import { MultiSelectArrowCIcon } from '~/assets/images';
import { fetchAdminPlansOptions, assignPlanToAccount } from '~/redux/actions/admin-pricing-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Styles
import './styles.scss';

const DATE_REGEX = /^\d{1,2}\/\d{1,2}\/\d{4}$/;

// Format date to YYYY-MM-DD format for API payloads
const formatDateForAPI = (date) => {
  if (!date) return null;
  if (typeof date === 'string') {
    // If it's already a string, extract just the date part
    return date.split('T')[0];
  }
  if (date instanceof Date) {
    // Format Date object to YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return null;
};

const AssignPlanModal = ({ open, handleClose, onOpenUpdatePlan, onPlanAssigned }) => {
  const dispatch = useDispatch();
  const [accountOptions, setAccountOptions] = useState([]);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [planOptions, setPlanOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [form, setForm] = useState({
    account: null,
    createMode: 'scratch', // 'scratch' | 'existing'
    planName: '',
    billingFrequency: 'monthly', // 'monthly' | 'annual'
    price: '',
    effectiveDate: null,
    notes: '',
    existingPlan: null,
  });
  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
  const [selectedEffectiveDate, setSelectedEffectiveDate] = useState(null);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  // Fetch options data when modal opens
  useEffect(() => {
    if (open) {
      fetchOptions();
    } else {
      // Reset all form fields when modal closes
      setForm({
        account: null,
        createMode: 'scratch',
        planName: '',
        billingFrequency: 'monthly',
        price: '',
        effectiveDate: null,
        notes: '',
        existingPlan: null,
      });
      setSelectedEffectiveDate(null);
      setIsDatepickerOpen(false);
      setSaveLoading(false);
      setAccountSearchTerm('');
    }
  }, [open]);

  const fetchOptions = async () => {
    try {
      setLoading(true);
      const response = await dispatch(fetchAdminPlansOptions());
      if (response.status === 200) {
        const { data } = response.data;

        // Map accounts for dropdown
        if (data.accounts && Array.isArray(data.accounts)) {
          const mappedAccounts = data.accounts.map((account) => ({
            id: account.id,
            title: account.label,
          }));
          setAccountOptions(mappedAccounts);
        }

        // Map plans for dropdown
        if (data.plans && Array.isArray(data.plans)) {
          const mappedPlans = data.plans.map((plan) => ({
            id: plan.id,
            title: plan.label,
            stripe_id: plan.stripe_id,
            amount: plan.amount,
          }));
          setPlanOptions(mappedPlans);
        }
      }
    } catch (error) {
      dispatch(failure('Failed to load options'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleDateChange = (date) => {
    setForm((prev) => ({ ...prev, effectiveDate: date }));
  };

  const handleToggle = (key, value) => () => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleCreateModeToggle = () => {
    setForm((prev) => ({
      ...prev,
      createMode: prev.createMode === 'scratch' ? 'existing' : 'scratch',
    }));
  };

  const handleBillingFrequencyToggle = () => {
    setForm((prev) => ({
      ...prev,
      billingFrequency: prev.billingFrequency === 'monthly' ? 'annual' : 'monthly',
    }));
  };

  const handleSave = async () => {
    // Prevent multiple submissions
    if (saveLoading) return;

    try {
      setSaveLoading(true);

      // Validate required fields
      if (!form.account) {
        dispatch(failure('Please select an account'));
        setSaveLoading(false);
        return;
      }

      if (form.createMode === 'scratch' && !form.planName) {
        dispatch(failure('Please enter a plan name'));
        setSaveLoading(false);
        return;
      }

      if (form.createMode === 'existing' && !form.existingPlan) {
        dispatch(failure('Please select an existing plan'));
        setSaveLoading(false);
        return;
      }

      if (form.createMode === 'scratch' && !form.price) {
        dispatch(failure('Please enter a price'));
        setSaveLoading(false);
        return;
      }

      if (!selectedEffectiveDate) {
        dispatch(failure('Please select an effective date'));
        setSaveLoading(false);
        return;
      }

      // Extract account ID from accountOptions using the selected value
      // OlcDropdownSearch with component="multipleIds" returns an object with id and title
      let accountId = null;
      if (form.account && form.account.id) {
        // If account is an object with id (from OlcDropdownSearch)
        accountId = form.account.id;
      } else if (Array.isArray(form.account) && form.account.length > 0) {
        // Fallback: If account is an array, get the first value (title/label)
        const accountTitle = form.account[0];
        // Find the matching option in accountOptions to get the ID
        const accountOption = accountOptions.find((acc) => acc.title === accountTitle);
        if (accountOption) {
          accountId = accountOption.id;
        }
      } else if (form.account && typeof form.account === 'string') {
        // Fallback: If account is a string, find in accountOptions
        const accountOption = accountOptions.find((acc) => acc.title === form.account);
        if (accountOption) {
          accountId = accountOption.id;
        }
      }

      if (!accountId) {
        dispatch(failure('Invalid account selection'));
        setSaveLoading(false);
        return;
      }

      // Extract plan ID from planOptions if using existing plan
      let planId = null;
      if (form.createMode === 'existing') {
        if (form.existingPlan && form.existingPlan.id) {
          // If existingPlan is an object with id (from OlcDropdownSearch)
          planId = form.existingPlan.id;
        } else if (Array.isArray(form.existingPlan) && form.existingPlan.length > 0) {
          // Fallback: If existingPlan is an array, get the first value
          const planStr = form.existingPlan[0];
          const planOption = planOptions.find((plan) => plan.title === planStr);
          if (planOption) {
            planId = planOption.id;
          }
        } else if (form.existingPlan && typeof form.existingPlan === 'string') {
          // Fallback: If existingPlan is a string, find in planOptions
          const planOption = planOptions.find((plan) => plan.title === form.existingPlan);
          if (planOption) {
            planId = planOption.id;
          }
        }
      }

      // Build payload according to API structure
      const payload = {
        org_id: accountId,
        plan_id: planId,
        plan_name: form.createMode === 'scratch' ? form.planName : String(planId),
        plan_duration_type: form.billingFrequency === 'monthly' ? 'monthly' : 'yearly',
        plan_amount: parseFloat(form.price) || 0,
        effective_date: formatDateForAPI(selectedEffectiveDate),
        notes: form.notes || '',
      };

      const response = await dispatch(assignPlanToAccount(payload));
      
      await new Promise((resolve) => setTimeout(resolve, 300));
      
      if (response.status === 200) {
        dispatch(success(response.data.message || 'Plan assigned successfully'));
        // Reset form
        setForm({
          account: null,
          createMode: 'scratch',
          planName: '',
          billingFrequency: 'monthly',
          price: '',
          effectiveDate: null,
          notes: '',
          existingPlan: null,
        });
        setSelectedEffectiveDate(null);
        setAccountSearchTerm('');
        // Refetch account data after successful assignment
        if (onPlanAssigned) {
          onPlanAssigned();
        }
        handleClose();
      } else {
        dispatch(failure(response?.data?.message || 'Failed to assign plan'));
      }
    } catch (error) {
      dispatch(
        failure(error?.response?.data?.message || error?.message || 'Failed to assign plan')
      );
    } finally {
      // Add small buffer before re-enabling button to avoid race conditions
      setTimeout(() => {
        setSaveLoading(false);
      }, 200);
    }
  };

  // Check if all required fields are filled
  const isSaveDisabled = () => {
    // Account is always required
    if (!account || !account.id) {
      return true;
    }

    // Effective Date is always required
    if (!selectedEffectiveDate) {
      return true;
    }

    // If creating from scratch, planName and price are required
    if (createMode === 'scratch') {
      if (!planName || planName.trim() === '') {
        return true;
      }
      if (!price || price.trim() === '') {
        return true;
      }
    }

    // If using existing plan, existingPlan is required (OlcDropdownSearch returns object with id and title)
    if (createMode === 'existing') {
      if (!existingPlan || !existingPlan.id) {
        return true;
      }
    }

    return false;
  };

  const {
    account,
    createMode,
    planName,
    billingFrequency,
    price,
    effectiveDate,
    notes,
    existingPlan,
  } = form;
  return (
    <OLCBaseModal
      open={open}
      onClose={handleClose}
      onSubmit={handleSave}
      title="Assign Plan"
      subTitle="Assign a plan to this account to define which features and tiered pricing it will have access to. The new features and pricing will take effect on the selected Effective Date. Once created, you'll be able to adjust each pricing tier under the Pricing Tiers tab."
      primaryText="Save"
      secondaryText="Cancel"
      width={640}
      paperClassName="assignPlanModalWrapper"
      loading={saveLoading}
      disablePrimary={isSaveDisabled()}
    >
      <Box className="assignPlanModalContent">
        <Box className="row">
          <InputLabel className="fieldLabel">Account to Assign Plan*</InputLabel>
          <Box className="existingPlanSelect">
            <OlcDropdownSearch
              options={
                accountSearchTerm.trim()
                  ? accountOptions // Show all options when searching (client-side filtering will handle it)
                  : accountOptions.slice(0, 10) // Show only first 10 when no search
              }
              selectedValue={account}
              setSelectedValue={(val) => setForm((prev) => ({ ...prev, account: val }))}
              placeHolderText="Select account"
              searchFieldPlaceholderText="accounts"
              noOptionText="No options found"
              component="multipleIds"
              searchAutocomplete
              clearable={true}
              onSearchChange={(searchValue) => {
                setAccountSearchTerm(searchValue || '');
              }}
            />
          </Box>
        </Box>

        <Box className="row">
          <InputLabel className="fieldLabel">How would you like to create this plan?</InputLabel>
          <Box className="segmentedRow">
            <Box className="segmentedControl createModeControl">
              <Box
                className={`segmentBtn ${createMode === 'scratch' ? 'active' : ''}`}
                onClick={() => setForm((prev) => ({ ...prev, createMode: 'scratch' }))}
              >
                Start from scratch
              </Box>
              <Box
                className={`segmentBtn ${createMode === 'existing' ? 'active' : ''}`}
                onClick={() => setForm((prev) => ({ ...prev, createMode: 'existing' }))}
              >
                From Existing Plan
              </Box>
            </Box>
          </Box>
        </Box>

        {createMode === 'scratch' && (
          <>
            <Box className="row">
              <InputLabel className="fieldLabel">Enter Plan Name*</InputLabel>
              <TextField
                className="textInput"
                placeholder="Enter plan name"
                value={planName}
                onChange={handleChange('planName')}
              />
            </Box>

            <Box className="row">
              <InputLabel className="fieldLabel">Plan Type</InputLabel>
              <Box className="segmentedRow">
                <Box className="segmentedControl planTypeControl">
                  <Box
                    className={`segmentBtn ${billingFrequency === 'monthly' ? 'active' : ''}`}
                    onClick={() => setForm((prev) => ({ ...prev, billingFrequency: 'monthly' }))}
                  >
                    Monthly Plan
                  </Box>
                  <Box
                    className={`segmentBtn ${billingFrequency === 'annual' ? 'active' : ''}`}
                    onClick={() => setForm((prev) => ({ ...prev, billingFrequency: 'annual' }))}
                  >
                    Annual Plan
                  </Box>
                </Box>
              </Box>
            </Box>
          </>
        )}

        {createMode === 'existing' && (
          <Box className="row">
            <InputLabel className="fieldLabel">Select Existing Plan*</InputLabel>
            <Box className="existingPlanSelect">
              <OlcDropdownSearch
                options={planOptions}
                selectedValue={existingPlan}
                setSelectedValue={(val) => setForm((prev) => ({ ...prev, existingPlan: val }))}
                placeHolderText="Select existing plan"
                searchFieldPlaceholderText="plans"
                clearable
              />
            </Box>
          </Box>
        )}

        {form.createMode === 'scratch' && (
          <Box className="row">
            <InputLabel className="fieldLabel">Enter Price*</InputLabel>
            <TextField
              className="textInput"
              placeholder="Enter price"
              type="number"
              value={price}
              onChange={handleChange('price')}
            />
          </Box>
        )}

        {/* <Box className="row">
          <InputLabel className="fieldLabel">Effective Date</InputLabel>
          <Box className="dateRow">
            <Box className="selectDate">
              <DatePicker
                selected={effectiveDate}
                onChange={handleDateChange}
                dateFormat="MM/dd/yyyy"
                className="datePicker"
                placeholderText="Select date"
                onBlur={(e) => {
                  if (e.target.value === '') {
                    handleDateChange(null);
                  }
                }}
              />
            </Box>
          </Box>
        </Box> */}
        <Box className="mailedDate newDatePicker updatedDesign">
          <Box className="selectDate">
            <InputLabel className="dateLabel newfieldsLabel">Effective Date*</InputLabel>
            <img
              src={MultiSelectArrowCIcon}
              alt="arrow"
              style={{ transform: isDatepickerOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
            <DatePicker
              minDate={tomorrow}
              selected={selectedEffectiveDate}
              onCalendarClose={() => setIsDatepickerOpen(false)}
              onChange={(date) => {
                setSelectedEffectiveDate(date);
              }}
              onFocus={() => setIsDatepickerOpen(true)}
              onBlur={() => setIsDatepickerOpen(false)}
              placeholderText="Select date"
              isClearable
              dateFormat="MM/dd/yyyy"
            />
          </Box>
        </Box>

        <Box className="row">
          <InputLabel className="fieldLabel">Notes</InputLabel>
          <TextField
            className="notesInput"
            multiline
            minRows={4}
            placeholder="Add any notes..."
            value={notes.trimStart()}
            onChange={handleChange('notes')}
          />
        </Box>
      </Box>
    </OLCBaseModal>
  );
};

export default AssignPlanModal;
